import React from 'react';
import { useTranslation } from 'react-i18next';
import { brandClasses } from '../../theme';
import { LanguageSelector } from '../LanguageSelector';

interface AuthLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

/**
 * AuthLayout - Split-screen layout for login/register pages
 *
 * Features:
 * - Split screen: Left info panel, right form card
 * - Warm, earthy color scheme from wireframe
 * - 2px borders matching design
 * - Rounded corners (18px cards, 23px buttons)
 * - Mobile-responsive (stacked on small screens)
 */
export const AuthLayout: React.FC<AuthLayoutProps> = ({ children, title, subtitle }) => {
  const { t } = useTranslation();

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-8 ${brandClasses.appShell}`}>
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* Left Panel - Welcome/Info Section */}
        <div className="hidden lg:flex flex-col justify-center">
          <div className={`rounded-2xl border-2 p-12 ${brandClasses.infoPanelSurface}`}>
            <h2 className={`mb-6 text-5xl font-bold ${brandClasses.titleText}`}>
              {t('layout.marketingTitle', { ns: 'auth' })}
            </h2>
            <p className={`mb-12 text-2xl font-medium ${brandClasses.mutedText}`}>
              {t('layout.marketingSubtitle', { ns: 'auth' })}
            </p>

            {/* Feature Badges */}
            <div className="space-y-4">
              <div className={`inline-block rounded-full border-2 px-5 py-2 ${brandClasses.chipSurface}`}>
                <span className={`text-sm font-semibold ${brandClasses.primaryText}`}>
                  {t('badges.personalFamilyUse', { ns: 'auth' })}
                </span>
              </div>
              <div className={`ms-4 inline-block rounded-full border-2 px-5 py-2 ${brandClasses.chipSurface}`}>
                <span className={`text-sm font-semibold ${brandClasses.primaryText}`}>
                  {t('badges.treeSharing', { ns: 'auth' })}
                </span>
              </div>
              <div className={`inline-block rounded-full border-2 px-5 py-2 ${brandClasses.chipSurface}`}>
                <span className={`text-sm font-semibold ${brandClasses.primaryText}`}>
                  {t('badges.enterpriseSecurity', { ns: 'auth' })}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Form Card */}
        <div className="flex flex-col justify-center">
          <div className="mb-4 flex justify-end">
            <LanguageSelector compact />
          </div>

          {/* Card Container */}
          <div className={`rounded-2xl border-2 p-8 sm:p-10 ${brandClasses.warmSurface} ${brandClasses.softBorder}`}>

            {/* Header */}
            {(title || subtitle) && (
              <div className="mb-8">
                {title && (
                  <h1 className={`mb-2 text-4xl font-bold ${brandClasses.primaryText}`}>
                    {title}
                  </h1>
                )}
                {subtitle && (
                  <p className={`text-lg font-semibold ${brandClasses.mutedText}`}>
                    {subtitle}
                  </p>
                )}
              </div>
            )}

            {/* Form Content */}
            <div className="space-y-6">
              {children}
            </div>

            {/* Footer */}
            <div className={`mt-8 border-t-2 pt-6 text-center text-xs ${brandClasses.mutedText} ${brandClasses.footerBorder}`}>
              <p>{t('app.copyright', { ns: 'common' })}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
